import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { PiEye, PiEyeSlash, PiChatCircleDotsBold, PiMagnifyingGlass } from "react-icons/pi";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { FaXmark } from "react-icons/fa6";
import {
  allowAddCommentOnMapAtom,
  commentsAtom,
  focusedCommentIdAtom,
  newlyCreatedCommentIdAtom,
  showCommentLabelsAtom,
  showCommentOnMapAtom,
} from "~/components/Map/Comment/CommentStore";
import { useAtom, useAtomValue } from 'jotai';
import { isMapLoadedDoneAtom, showCommentPanelAtom } from "../../stores/mapStore";
import CommentItem, { CommentItemMode } from "./CommentItem/CommentItem";
import { IoEllipsisHorizontal } from "react-icons/io5";
import { Comment_AddComment_RequestBody, CommentAPIs } from "~/common/typescript/apis/CommentAPIs";
import { currentProjectAtom } from "~/stores/ProjectStore";
import { userInformationAtom } from "~/stores/UserStore";
import mouse_plus from "~/assets/svg/mouse_plus.svg";
import { Project_Base } from "~/common/typescript/models/Project";
import { Checkbox, Empty, Input, Popover, Segmented, Tooltip } from "antd";
import { CommentMapUtilOptimized } from "../../Comment/CommentMapUtilOptimized";
import { CommentItemUtil } from "./CommentItem/CommentItemUtil";
import { TbMapPlus } from "react-icons/tb";

let _showCommentPanel = false;
let _showCommentOnMap = false;
let _currentProject: Project_Base | undefined;

type CommentStatusFilter = 'all' | 'unresolved' | 'resolved';

const CommentPanel = () => {
  const [allowAddCommentOnMap, setAllowAddCommentOnMap] = useAtom(allowAddCommentOnMapAtom);
  const [showCommentOnMap, setshowCommentOnMap] = useAtom(showCommentOnMapAtom);
  const [showCommentLabels, setShowCommentLabels] = useAtom(showCommentLabelsAtom);
  const [showCommentPanel, setShowCommentPanel] = useAtom<boolean>(showCommentPanelAtom);
  const [currentProject] = useAtom(currentProjectAtom);
  const loggedUser = useAtomValue(userInformationAtom);
  const [comments, setComments] = useAtom(commentsAtom);
  const [newlyCreatedCommentId, setNewlyCreatedCommentId] = useAtom(newlyCreatedCommentIdAtom);
  const [focusedCommentId, setFocusedCommentId] = useAtom(focusedCommentIdAtom);
  const [isMapLoadedDone] = useAtom(isMapLoadedDoneAtom);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<CommentStatusFilter>('all');

  useEffect(() => {
    _showCommentPanel = showCommentPanel;
  }, [showCommentPanel]);

  useEffect(() => {
    _showCommentOnMap = showCommentOnMap;
  }, [showCommentOnMap]);

  useEffect(() => {
    _currentProject = currentProject;
  }, [currentProject]);

  const activeComments = useMemo(
    () => comments.filter((comment) => !comment.is_deleted),
    [comments],
  );

  const filteredComments = useMemo(() => {
    const q = searchKeyword.trim().toLowerCase();
    return activeComments.filter((comment) => {
      if (statusFilter === 'unresolved' && comment.resolved) return false;
      if (statusFilter === 'resolved' && !comment.resolved) return false;
      if (!q) return true;
      const titleMatch = (comment.title || '').toLowerCase().includes(q);
      const descMatch = (comment.description || '').toLowerCase().includes(q);
      const authorMatch = (comment.user?.fullname || comment.user?.username || '').toLowerCase().includes(q);
      return titleMatch || descMatch || authorMatch;
    });
  }, [activeComments, searchKeyword, statusFilter]);

  const scrollToCommentItem = useCallback(
    (targetId: number) => {
      const targetComment = activeComments.find((c) => Number(c.id) === Number(targetId));
      if (!targetComment) return;

      // Ensure filters don't hide the focused comment
      if (
        (statusFilter === 'unresolved' && targetComment.resolved) ||
        (statusFilter === 'resolved' && !targetComment.resolved)
      ) {
        setStatusFilter('all');
      }
      if (searchKeyword.trim()) {
        const q = searchKeyword.trim().toLowerCase();
        const matchesSearch =
          (targetComment.title || '').toLowerCase().includes(q) ||
          (targetComment.description || '').toLowerCase().includes(q) ||
          (targetComment.user?.fullname || targetComment.user?.username || '').toLowerCase().includes(q);
        if (!matchesSearch) {
          setSearchKeyword('');
        }
      }

      setTimeout(() => {
        const container = listContainerRef.current;
        const itemEl = (container || document).querySelector(
          `[data-testid="comment-item-${targetId}"]`,
        ) as HTMLElement | null;
        if (itemEl) {
          itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 60);
    },
    [activeComments, statusFilter, searchKeyword],
  );

  useEffect(() => {
    const targetId = newlyCreatedCommentId || focusedCommentId;
    if (targetId) {
      scrollToCommentItem(targetId);
    }
  }, [newlyCreatedCommentId, focusedCommentId, scrollToCommentItem]);

  useEffect(() => {
    const handleScrollEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ commentId?: number }>;
      const targetId = Number(customEvent.detail?.commentId);
      if (targetId) {
        setFocusedCommentId(targetId);
        scrollToCommentItem(targetId);
      }
    };
    window.addEventListener('geocloud:scroll-to-comment', handleScrollEvent);
    return () => {
      window.removeEventListener('geocloud:scroll-to-comment', handleScrollEvent);
    };
  }, [scrollToCommentItem, setFocusedCommentId]);

  const onClick = async (e: any) => {
    if (!_showCommentPanel) return;
    if (!_showCommentOnMap) return;
    if (!isMapLoadedDone) return;
    if (!window.map) return;

    const features = window.map.queryRenderedFeatures(e.point, {
      layers: [CommentMapUtilOptimized._layerId],
    });

    if (features && features.length > 0) {
      return;
    } else {
      if (!allowAddCommentOnMap) return;
      const lngLat = e.lngLat;
      const payload: Comment_AddComment_RequestBody = {
        projectId: _currentProject?.id as number,
        title: "Chưa điền tiêu đề",
        description: "Chưa điền mô tả",
        geojson: JSON.stringify({
          type: "point",
          coordinates: [lngLat.lng, lngLat.lat],
        }),
      };
      const result = await CommentAPIs.remote_addComment(payload);
      if (result.data) {
        const created = {
          ...result.data,
          user: result.data.user || loggedUser || undefined,
        };
        setComments([...comments, created]);
        setFocusedCommentId(created.id!);
        setNewlyCreatedCommentId(created.id!);
      }
    }
  };

  const onMouseEnter = async () => {
    if (!_showCommentPanel) return;
    if (!_showCommentOnMap) return;
    if (!isMapLoadedDone) return;
    if (!allowAddCommentOnMap) return;
    if (window.map) {
      window.map.getCanvas().style.cursor = `url('${mouse_plus}') 0 0, pointer`;
    }
  };

  const onMouseLeave = async () => {
    if (!isMapLoadedDone) return;
    if (window.map) {
      window.map.getCanvas().style.cursor = 'pointer';
    }
  };

  useEffect(() => {
    if (window.map) {
      const map = window.map;
      map.on('click', onClick);
      map.getCanvas().addEventListener('mouseenter', onMouseEnter);
      map.getCanvas().addEventListener('mouseleave', onMouseLeave);
    }
    return () => {
      if (window.map) {
        window.map.off('click', onClick);
        window.map.getCanvas().removeEventListener('mouseenter', onMouseEnter);
        window.map.getCanvas().removeEventListener('mouseleave', onMouseLeave);
      }
    };
  }, [isMapLoadedDone, comments, allowAddCommentOnMap]);

  const moreMenuContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 4, minWidth: 200 }}>
      <Checkbox
        checked={showCommentLabels}
        onChange={(e) => setShowCommentLabels(e.target.checked)}
      >
        Hiển thị nhãn tiêu đề trên bản đồ
      </Checkbox>
    </div>
  );

  return (
    <UI_Wrapper data-testid="comment-panel">
      <UI_Header>
        <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PiChatCircleDotsBold style={{ color: '#2563eb', fontSize: '18px' }} />
          <span>Ghi chú ({activeComments.length})</span>
        </UI_HeaderLeft>
        <UI_HeaderRight>
          <Tooltip title="Cho phép thêm ghi chú trên bản đồ">
            <UI_HeaderRight_item
              data-testid="comment-panel-add-on-map-btn"
              className={allowAddCommentOnMap ? 'active' : ''}
              onClick={() => setAllowAddCommentOnMap(!allowAddCommentOnMap)}
            >
              <TbMapPlus />
            </UI_HeaderRight_item>
          </Tooltip>
          <Tooltip title="Hiển thị/Ẩn ghi chú trên bản đồ">
            <UI_HeaderRight_item
              data-testid="comment-panel-toggle-visibility-btn"
              onClick={() => setshowCommentOnMap(!showCommentOnMap)}
            >
              {showCommentOnMap ? <PiEye /> : <PiEyeSlash />}
            </UI_HeaderRight_item>
          </Tooltip>
          <Popover content={moreMenuContent} trigger="click" placement="bottomRight">
            <Tooltip title="Công cụ khác">
              <UI_HeaderRight_item data-testid="comment-panel-more-btn">
                <IoEllipsisHorizontal />
              </UI_HeaderRight_item>
            </Tooltip>
          </Popover>
          <Tooltip title="Đóng bảng ds ghi chú">
            <UI_HeaderRight_item
              data-testid="comment-panel-close-btn"
              onClick={() => {
                setShowCommentPanel(false);
              }}
            >
              <FaXmark />
            </UI_HeaderRight_item>
          </Tooltip>
        </UI_HeaderRight>
      </UI_Header>

      <UI_FilterBar>
        <Input
          size="small"
          allowClear
          prefix={<PiMagnifyingGlass style={{ color: '#94a3b8' }} />}
          placeholder="Tìm tiêu đề, nội dung, @tên..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          data-testid="comment-panel-search-input"
        />
        <Segmented
          size="small"
          block
          value={statusFilter}
          onChange={(val) => setStatusFilter(val as CommentStatusFilter)}
          options={[
            { label: `Tất cả (${activeComments.length})`, value: 'all' },
            { label: `Chưa xong (${activeComments.filter((c) => !c.resolved).length})`, value: 'unresolved' },
            { label: `Đã xong (${activeComments.filter((c) => c.resolved).length})`, value: 'resolved' },
          ]}
        />
      </UI_FilterBar>

      <UI_List ref={listContainerRef} data-testid="comment-panel-list">
        {filteredComments.map((comment) => (
          <UI_Item_Wrapper
            key={comment.id}
            $isFocused={comment.id === focusedCommentId}
            data-focused={comment.id === focusedCommentId ? 'true' : 'false'}
            onClick={() => {
              setFocusedCommentId(comment.id!);
            }}
            onDoubleClick={() => {
              setFocusedCommentId(comment.id!);
              CommentItemUtil.zoomToComment(comment);
            }}
          >
            <CommentItem
              comment={comment}
              mode={comment.id === newlyCreatedCommentId ? CommentItemMode.Edit : CommentItemMode.View}
            />
          </UI_Item_Wrapper>
        ))}
        {filteredComments.length === 0 && (
          <div style={{ padding: 16, color: '#94a3b8' }}>
            <Empty description="Không có ghi chú phù hợp" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
      </UI_List>
    </UI_Wrapper>
  );
};

export default memo(CommentPanel);

const UI_Wrapper = styled.div`
  width: 340px;
  height: 540px;
  max-height: calc(100% - 32px);
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  box-shadow: var(--shadows-paneShadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 950;
`;

const UI_FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  background: rgba(248, 250, 252, 0.85);
`;

const UI_List = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const UI_Item_Wrapper = styled.div<{ $isFocused?: boolean }>`
  width: 100%;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  background-color: ${(p) => (p.$isFocused ? '#eff6ff' : 'transparent')};
  box-shadow: ${(p) => (p.$isFocused ? 'inset 3px 0 0 #2563eb' : 'none')};
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    background-color: ${(p) => (p.$isFocused ? '#e0f2fe' : '#f8fafc')};
  }
`;
